import { useEffect, useRef, useState, type FormEvent } from 'react'
import type { Action } from '../../domain/action'
import type { Area } from '../../domain/area'
import { levelFor } from '../../domain/area'
import type { Specialization } from '../../domain/specialization'
import { AddIcon, CloseIcon, LabelIcon, TrashIcon } from './Icons'
import { useI18n } from '../../i18n'

interface Props {
  area: Area | null
  specializations: Specialization[]
  actions: Action[]
  open: boolean
  saving: boolean
  error: string | null
  onClose: () => void
  onAdd: (name: string) => Promise<boolean>
  onDelete: (item: Specialization) => Promise<boolean>
}

export const SpecializationsSheet = ({ area, specializations, actions, open, saving, error, onClose, onAdd, onDelete }: Props) => {
  const { copy: { common, skills } } = useI18n()
  const dialogRef = useRef<HTMLDialogElement>(null)
  const [name, setName] = useState('')
  const [pendingDelete, setPendingDelete] = useState<Specialization | null>(null)
  useEffect(() => {
    const dialog = dialogRef.current
    if (!dialog) return
    if (open && !dialog.open) dialog.showModal()
    if (!open && dialog.open) dialog.close()
  }, [open])
  useEffect(() => { if (open) { setName(''); setPendingDelete(null) } }, [open])
  if (!area) return null
  const normalized = name.trim()
  const valid = normalized.length > 0 && !specializations.some((item) => item.name.toLocaleLowerCase() === normalized.toLocaleLowerCase())
  const submit = async (event: FormEvent) => {
    event.preventDefault()
    if (valid && await onAdd(normalized)) setName('')
  }
  return (
    <dialog ref={dialogRef} className="sheet specializations-sheet" onCancel={(event) => { event.preventDefault(); onClose() }} onClose={onClose}>
      <div className="sheet__body specializations-sheet__body">
        <div className="sheet__handle" />
        <div className="sheet__heading"><div><h2>{skills.title} {area.name}</h2><p>{skills.explanation}</p></div><button className="icon-button" type="button" onClick={onClose} aria-label={common.close}><CloseIcon /></button></div>
        <form className="specialization-new" onSubmit={submit}>
          <label className="field"><span>{skills.newSkill}</span><input value={name} onChange={(event) => setName(event.target.value.slice(0, 30))} placeholder={skills.example} /></label>
          <button className="icon-button specialization-new__button" type="submit" disabled={!valid || saving} aria-label={skills.create}><AddIcon /></button>
        </form>
        <p className="eyebrow specialization-count">ESPECIALIZACIONES ({specializations.length})</p>
        {error && <div className="error-message" role="alert">{error}</div>}
        {specializations.length === 0 ? <div className="specializations-empty">{skills.empty}</div> : (
          <div className="specializations-list">{specializations.map((item) => {
            const count = actions.filter((action) => action.categoryIds.includes(item.id)).length
            return <div key={item.id} className="specialization-manage-row"><LabelIcon style={{ color: area.color }} /><div><strong>{item.name}</strong><small>Lvl. {levelFor(item.xp)} · {count} {count === 1 ? common.action : common.actions}</small></div><button className="icon-button" type="button" onClick={() => setPendingDelete(item)} aria-label={`${common.delete} ${item.name}`}><TrashIcon /></button></div>
          })}</div>
        )}
        <p className="sheet__note">{skills.note}</p>
        {pendingDelete && <div className="delete-confirm specialization-delete"><p><strong>{skills.deleteTitle} {pendingDelete.name}</strong><br />{skills.deleteHelp}</p><div><button className="button button--ghost" type="button" onClick={() => setPendingDelete(null)}>{common.cancel}</button><button className="button button--danger" type="button" disabled={saving} onClick={async () => { if (await onDelete(pendingDelete)) setPendingDelete(null) }}>{common.delete}</button></div></div>}
      </div>
    </dialog>
  )
}
